import type { ApplicationRecord } from '../types/domain'

export const megaMajorApplications: ApplicationRecord[] = [
  {
    id: 'app-1',
    ref: 'SDU-MEG-2026-014',
    eventName: 'FIFA World Cup 2038 — UAE Host Bid',
    entityId: 'en-4',
    track: 'mega',
    hostBody: 'FIFA',
    emirate: 'Federal',
    sector: 'Sport',
    requestedSupportAed: 4_800_000_000,
    editionYear: 2038,
    stage: 'bid',
    status: 'active',
    stageEnteredOn: '2026-05-04',
    daysInStage: 106,
    slaDueOn: '2026-09-10',
    officer: 'Khalid Al Marzouqi',
    aiScore: 81.4,
    summary:
      'Federal bid for a 48-team tournament across five emirates, anchored on eight stadiums with two new builds. Bid book submission window closes with FIFA in November 2026.',
    criteria: [
      { id: 'host-requirements', label: 'Host-body requirement coverage', weight: 35, score: 84, evidence: '41 of 48 mandatory FIFA requirements evidenced in the technical volumes.' },
      { id: 'guarantees', label: 'Government guarantees issued', weight: 25, score: 72, evidence: '7 of 10 guarantee letters signed; tax exemption and work-permit letters pending.' },
      { id: 'dossier', label: 'Bid dossier completeness', weight: 25, score: 88, evidence: 'Volumes 1–4 complete; venue master plan issued Rev C.' },
      { id: 'competitive', label: 'Competitive position', weight: 15, score: 76, evidence: 'Strong on venues and legacy; weaker on existing accommodation stock versus joint bids.' }
    ],
    flags: [
      { id: 'fl-1', severity: 'critical', label: 'Tax exemption guarantee outstanding', detail: 'FIFA requires an unconditional federal tax exemption letter 120 days before submission. Ministry of Finance draft is at second review.' },
      { id: 'fl-2', severity: 'high', label: 'Accommodation capacity gap', detail: 'Modelled peak demand exceeds committed room nights by 18,400 in the northern cluster.' }
    ],
    recommendations: [
      { id: 're-1', ruleId: 'rr-3', title: 'Escalate the tax exemption guarantee to the federal alignment committee', detail: 'Requirement is mandatory and within 90 days of the submission deadline; escalation historically clears drafting within 3 weeks.', confidence: 92, stage: 'bid', status: 'pending' },
      { id: 're-2', ruleId: 'rr-4', title: 'Commission a differentiation study on legacy and sustainability', detail: 'Two competing bids outperform on accommodation. Legacy and net-zero operations are the strongest available differentiators.', confidence: 78, stage: 'bid', status: 'pending' },
      { id: 're-3', ruleId: 'rr-2', title: 'Attach an SDU delivery partner to the host cities workstream', detail: 'Three of five host emirates have no prior tournament-scale delivery record.', confidence: 71, stage: 'bid', status: 'accepted' }
    ],
    documents: [
      { id: 'do-1', label: 'Bid Volume 1 — Vision & Concept', status: 'verified', updatedOn: '2026-06-12' },
      { id: 'do-2', label: 'Bid Volume 2 — Venues & Infrastructure', status: 'verified', updatedOn: '2026-07-02' },
      { id: 'do-3', label: 'Bid Volume 3 — Operations & Services', status: 'uploaded', updatedOn: '2026-08-04' },
      { id: 'do-4', label: 'Federal tax exemption guarantee', status: 'missing', updatedOn: '2026-08-01' },
      { id: 'do-5', label: 'Sustainability & carbon plan', status: 'uploaded', updatedOn: '2026-07-28' }
    ],
    timeline: [
      { id: 'tl-1', on: '2026-02-11', actor: 'Fatima Al Hosani', label: 'Application received', detail: 'Submitted by UAE National Olympic Committee on behalf of the federal bid office.' },
      { id: 'tl-2', on: '2026-03-02', actor: 'AI Assessment Engine', label: 'Assessment score generated', detail: 'Weighted score 83.9 — auto-advance band.' },
      { id: 'tl-3', on: '2026-05-04', actor: 'Hend AlMheiri', label: 'Advanced to Bid', detail: 'Assessment gate cleared with all artefacts met.' },
      { id: 'tl-4', on: '2026-07-22', actor: 'Khalid Al Marzouqi', label: 'Recommendation accepted', detail: 'SDU delivery partner attached to host cities workstream.' }
    ],
    gates: {
      assessment: [
        { id: 'g-1', label: 'Eligibility screening completed', kind: 'artefact', status: 'met', owner: 'SDU Assessment' },
        { id: 'g-2', label: 'Economic impact model reviewed', kind: 'artefact', status: 'met', owner: 'Ministry of Economy' },
        { id: 'g-3', label: 'Director assessment endorsement', kind: 'signoff', status: 'met', owner: 'Hend AlMheiri' }
      ],
      bid: [
        { id: 'g-4', label: 'Host-body requirement matrix at 85% or above', kind: 'artefact', status: 'partial', owner: 'Bid Office', note: 'Currently 84% — one mandatory requirement short.' },
        { id: 'g-5', label: 'Bid dossier volumes 1–4 issued', kind: 'artefact', status: 'met', owner: 'Bid Office' },
        { id: 'g-6', label: 'All government guarantee letters signed', kind: 'signoff', status: 'outstanding', owner: 'Ministry of Finance', note: 'Tax exemption and work-permit letters in drafting.' },
        { id: 'g-7', label: 'Head of Bid Assessment endorsement', kind: 'signoff', status: 'partial', owner: 'Khalid Al Marzouqi' }
      ]
    },
    bid: {
      hostBody: 'FIFA',
      submissionDeadline: '2026-11-30',
      readiness: 84,
      requirements: [
        { id: 'rq-1', label: 'Eight stadiums with minimum 40,000 capacity', status: 'met', note: '6 existing, 2 in design — capacity certified.' },
        { id: 'rq-2', label: '72 team base camps to FIFA specification', status: 'partial', note: '58 confirmed, 14 under assessment in the northern emirates.' },
        { id: 'rq-3', label: 'Federal tax exemption guarantee', status: 'outstanding', note: 'Mandatory. Draft at Ministry of Finance second review.' },
        { id: 'rq-4', label: 'Visa and entry facilitation guarantee', status: 'met', note: 'Issued by ICP on 14 Jun 2026.' },
        { id: 'rq-5', label: 'Broadcast and media infrastructure plan', status: 'met', note: 'IBC site at DWTC confirmed with fibre redundancy.' },
        { id: 'rq-6', label: 'Human rights and labour compliance framework', status: 'partial', note: 'Independent monitoring body appointed; baseline study pending.' },
        { id: 'rq-7', label: 'Accommodation — 120,000 committed room nights', status: 'outstanding', note: '18,400 room-night shortfall in the northern cluster.' }
      ],
      dossier: [
        { id: 'bd-1', label: 'Volume 1 — Vision, Concept & Legacy', status: 'verified', updatedOn: '2026-06-12' },
        { id: 'bd-2', label: 'Volume 2 — Venues & Infrastructure', status: 'verified', updatedOn: '2026-07-02' },
        { id: 'bd-3', label: 'Volume 3 — Operations & Services', status: 'uploaded', updatedOn: '2026-08-04' },
        { id: 'bd-4', label: 'Volume 4 — Commercial & Financial', status: 'uploaded', updatedOn: '2026-08-09' },
        { id: 'bd-5', label: 'Volume 5 — Sustainability & Human Rights', status: 'missing', updatedOn: '2026-08-01' }
      ],
      guarantees: [
        { id: 'gu-1', label: 'Security and public safety guarantee', issuer: 'Ministry of Interior', status: 'met' },
        { id: 'gu-2', label: 'Customs and temporary import guarantee', issuer: 'Federal Customs Authority', status: 'met' },
        { id: 'gu-3', label: 'Visa and entry facilitation', issuer: 'ICP', status: 'met' },
        { id: 'gu-4', label: 'Federal tax exemption', issuer: 'Ministry of Finance', status: 'outstanding' },
        { id: 'gu-5', label: 'Work permit and labour mobility', issuer: 'MoHRE', status: 'partial' }
      ],
      competitors: [
        { city: 'Riyadh', country: 'Saudi Arabia', strength: 88, note: 'Stronger accommodation pipeline, comparable venue programme.' },
        { city: 'Casablanca cluster', country: 'Morocco', strength: 74, note: 'Joint bid with lower cost base, weaker transport connectivity.' },
        { city: 'Buenos Aires cluster', country: 'Argentina', strength: 69, note: 'Heritage narrative advantage, infrastructure funding uncertain.' }
      ]
    }
  },
  {
    id: 'app-2',
    ref: 'SDU-MEG-2026-003',
    eventName: 'World Expo 2035 — Dubai Second Edition',
    entityId: 'en-2',
    track: 'mega',
    hostBody: 'Bureau International des Expositions',
    emirate: 'Dubai',
    sector: 'Trade & Exhibitions',
    requestedSupportAed: 6_200_000_000,
    editionYear: 2035,
    stage: 'approval',
    status: 'active',
    stageEnteredOn: '2026-07-21',
    daysInStage: 28,
    slaDueOn: '2026-08-29',
    officer: 'Hend AlMheiri',
    aiScore: 88.6,
    summary:
      'Second-edition Expo proposal reusing 71% of the 2020 site with a new innovation district. Bid submitted to the BIE; approval stage covers the federal funding mandate and cabinet decision record.',
    criteria: [
      { id: 'strategic-fit', label: 'Strategic fit with national agenda', weight: 25, score: 94, evidence: 'Direct line to We the UAE 2031 economic and soft-power pillars.' },
      { id: 'economic-impact', label: 'Economic impact & GDP contribution', weight: 20, score: 91, evidence: 'Modelled AED 41B gross value add over 8 years; 2.1x on requested support.' },
      { id: 'entity-capability', label: 'Entity delivery capability', weight: 15, score: 96, evidence: 'DWTC delivered Expo 2020 legacy operations and 12 world congresses.' },
      { id: 'infrastructure', label: 'Infrastructure & venue readiness', weight: 15, score: 89, evidence: '71% asset reuse; metro extension already funded.' },
      { id: 'financial', label: 'Financial viability & co-funding', weight: 15, score: 78, evidence: 'Co-funding at 34%; commercial partner pipeline at early stage.' },
      { id: 'legacy', label: 'Legacy & sustainability', weight: 10, score: 82, evidence: 'Net-zero operations commitment; district handover plan drafted.' }
    ],
    flags: [
      { id: 'fl-3', severity: 'medium', label: 'Commercial partner pipeline early', detail: 'Only 3 of 14 target premier partners have signed letters of intent.' }
    ],
    recommendations: [
      { id: 're-4', ruleId: 'rr-5', title: 'Approve conditionally pending Ministry of Energy sign-off', detail: 'All other mandatory sign-offs are captured; conditional approval preserves the BIE timeline.', confidence: 86, stage: 'approval', status: 'pending' },
      { id: 're-5', ruleId: 'rr-1', title: 'Require a revised commercial co-funding structure', detail: 'Co-funding at 34% against a 40% target for Mega events of this envelope.', confidence: 74, stage: 'approval', status: 'pending' }
    ],
    documents: [
      { id: 'do-6', label: 'BIE candidature dossier', status: 'verified', updatedOn: '2026-06-30' },
      { id: 'do-7', label: 'Federal funding request', status: 'verified', updatedOn: '2026-07-18' },
      { id: 'do-8', label: 'Cabinet decision memorandum', status: 'uploaded', updatedOn: '2026-08-11' },
      { id: 'do-9', label: 'Independent economic impact assurance', status: 'verified', updatedOn: '2026-05-22' }
    ],
    timeline: [
      { id: 'tl-5', on: '2026-01-14', actor: 'Fatima Al Hosani', label: 'Application received' },
      { id: 'tl-6', on: '2026-02-03', actor: 'AI Assessment Engine', label: 'Assessment score generated', detail: 'Weighted score 88.6 — auto-advance band.' },
      { id: 'tl-7', on: '2026-03-19', actor: 'Hend AlMheiri', label: 'Advanced to Bid' },
      { id: 'tl-8', on: '2026-07-21', actor: 'Hend AlMheiri', label: 'Advanced to Approval', detail: 'Bid gate cleared; candidature dossier lodged with the BIE.' }
    ],
    gates: {
      assessment: [
        { id: 'g-8', label: 'Eligibility screening completed', kind: 'artefact', status: 'met', owner: 'SDU Assessment' },
        { id: 'g-9', label: 'Economic impact model assured', kind: 'artefact', status: 'met', owner: 'Ministry of Economy' },
        { id: 'g-10', label: 'Director assessment endorsement', kind: 'signoff', status: 'met', owner: 'Hend AlMheiri' }
      ],
      bid: [
        { id: 'g-11', label: 'Candidature dossier lodged', kind: 'artefact', status: 'met', owner: 'DWTC Bid Office' },
        { id: 'g-12', label: 'Host-body requirement coverage above 85%', kind: 'artefact', status: 'met', owner: 'DWTC Bid Office' },
        { id: 'g-13', label: 'Guarantee letters signed', kind: 'signoff', status: 'met', owner: 'Ministry of Finance' }
      ],
      approval: [
        { id: 'g-14', label: 'Funding mandate memorandum issued', kind: 'artefact', status: 'met', owner: 'Ministry of Finance' },
        { id: 'g-15', label: 'Committee review minutes recorded', kind: 'artefact', status: 'met', owner: 'SDU Secretariat' },
        { id: 'g-16', label: 'Ministry of Energy & Infrastructure sign-off', kind: 'signoff', status: 'outstanding', owner: 'MoEI', note: 'Awaiting utilities capacity confirmation for the innovation district.' },
        { id: 'g-17', label: 'Cabinet decision record', kind: 'signoff', status: 'partial', owner: 'Cabinet Affairs', note: 'Scheduled for the 2 September session.' }
      ]
    },
    bid: {
      hostBody: 'Bureau International des Expositions',
      submissionDeadline: '2026-06-30',
      readiness: 96,
      requirements: [
        { id: 'rq-8', label: 'Site of minimum 200 hectares secured', status: 'met', note: 'Expo 2020 site plus 46ha innovation district.' },
        { id: 'rq-9', label: 'State-level participation guarantee', status: 'met', note: 'Issued by MoFA on 3 May 2026.' },
        { id: 'rq-10', label: 'Participant pavilion funding scheme', status: 'met', note: 'AED 900M developing-nation support fund committed.' },
        { id: 'rq-11', label: 'Registration dossier and theme statement', status: 'met', note: 'Accepted by the BIE Executive Committee.' }
      ],
      dossier: [
        { id: 'bd-6', label: 'Candidature dossier', status: 'verified', updatedOn: '2026-06-30' },
        { id: 'bd-7', label: 'Theme development plan', status: 'verified', updatedOn: '2026-06-11' },
        { id: 'bd-8', label: 'Site master plan Rev D', status: 'verified', updatedOn: '2026-06-24' }
      ],
      guarantees: [
        { id: 'gu-6', label: 'State participation guarantee', issuer: 'Ministry of Foreign Affairs', status: 'met' },
        { id: 'gu-7', label: 'Financial guarantee', issuer: 'Ministry of Finance', status: 'met' },
        { id: 'gu-8', label: 'Site availability guarantee', issuer: 'Government of Dubai', status: 'met' }
      ],
      competitors: [
        { city: 'Osaka', country: 'Japan', strength: 71, note: 'Withdrew from the 2035 cycle in April 2026.' },
        { city: 'Warsaw', country: 'Poland', strength: 64, note: 'Funding mandate not yet secured at national level.' }
      ]
    },
    approval: {
      committeeDate: '2026-09-02',
      mandateAed: 6_200_000_000,
      decision: 'pending',
      conditions: [
        'Commercial co-funding to reach 40% of total budget before Plan & Build gate',
        'Utilities capacity confirmation for the innovation district'
      ],
      stakeholders: [
        { id: 'sh-1', body: 'Ministry of Finance', representative: 'Undersecretary, Budget', status: 'signed', on: '2026-08-04' },
        { id: 'sh-2', body: 'Government of Dubai — Executive Council', representative: 'Secretary General', status: 'signed', on: '2026-07-30' },
        { id: 'sh-3', body: 'Ministry of Energy & Infrastructure', representative: 'Director, Utilities Planning', status: 'pending' },
        { id: 'sh-4', body: 'Ministry of Interior', representative: 'Major Events Security Command', status: 'signed', on: '2026-08-07' },
        { id: 'sh-5', body: 'GCAA — Aviation', representative: 'Director, Airspace', status: 'signed', on: '2026-08-01' }
      ]
    }
  },
  {
    id: 'app-3',
    ref: 'SDU-MAJ-2025-041',
    eventName: 'COP38 Climate Change Conference',
    entityId: 'en-3',
    track: 'major',
    hostBody: 'UNFCCC',
    emirate: 'Abu Dhabi',
    sector: 'Environment & Climate',
    requestedSupportAed: 1_950_000_000,
    editionYear: 2033,
    stage: 'plan-build',
    status: 'active',
    stageEnteredOn: '2026-04-02',
    daysInStage: 138,
    slaDueOn: '2026-09-30',
    officer: 'Omar Bin Sulayem',
    aiScore: 85.2,
    summary:
      'Hosting rights secured for the 38th Conference of the Parties. Plan & Build stage covers the blue-zone overlay, delegate logistics for 84,000 accredited participants and the negotiation venue programme.',
    criteria: [
      { id: 'strategic-fit', label: 'Sector strategy alignment', weight: 25, score: 93, evidence: 'Core to the UAE Net Zero 2050 diplomatic agenda.' },
      { id: 'economic-impact', label: 'Economic & reputational impact', weight: 20, score: 87, evidence: '212,000 international visitor nights modelled.' },
      { id: 'entity-capability', label: 'Entity delivery capability', weight: 20, score: 84, evidence: 'MOCCAE delivered COP28 host-country operations.' },
      { id: 'infrastructure', label: 'Venue & logistics fit', weight: 15, score: 81, evidence: 'ADNEC blue zone with temporary overlay; transport modelling in progress.' },
      { id: 'financial', label: 'Financial viability', weight: 20, score: 79, evidence: 'Federal mandate confirmed; in-kind partner contributions unconfirmed.' }
    ],
    flags: [
      { id: 'fl-4', severity: 'high', label: 'Budget drawn ahead of schedule completion', detail: 'AED 742M drawn (38%) against 26% schedule completion at the mid-point of Plan & Build.' },
      { id: 'fl-5', severity: 'medium', label: 'Accreditation platform procurement delayed', detail: 'Tender award slipped 6 weeks; integration testing window compressed.' }
    ],
    recommendations: [
      { id: 're-6', ruleId: 'rr-6', title: 'Trigger a cost-to-complete review before the Plan & Build gate', detail: 'Draw-down to completion ratio exceeds the 1.4 threshold used for Major events.', confidence: 83, stage: 'plan-build', status: 'pending' },
      { id: 're-7', ruleId: 'rr-2', title: 'Reassign the accreditation workstream to the SDU technology cell', detail: 'Procurement slippage places the integration test window inside the Readiness stage.', confidence: 69, stage: 'plan-build', status: 'pending' }
    ],
    documents: [
      { id: 'do-10', label: 'Host country agreement', status: 'verified', updatedOn: '2026-02-19' },
      { id: 'do-11', label: 'Integrated master schedule Rev 4', status: 'uploaded', updatedOn: '2026-08-06' },
      { id: 'do-12', label: 'Blue zone overlay design pack', status: 'uploaded', updatedOn: '2026-07-14' },
      { id: 'do-13', label: 'Procurement compliance certificate', status: 'rejected', updatedOn: '2026-07-29' }
    ],
    timeline: [
      { id: 'tl-9', on: '2025-09-08', actor: 'Fatima Al Hosani', label: 'Application received' },
      { id: 'tl-10', on: '2025-10-01', actor: 'AI Assessment Engine', label: 'Assessment score generated', detail: 'Weighted score 85.2 — auto-advance band.' },
      { id: 'tl-11', on: '2025-11-12', actor: 'Hend AlMheiri', label: 'Advanced to Bid' },
      { id: 'tl-12', on: '2026-02-19', actor: 'Hend AlMheiri', label: 'Advanced to Approval', detail: 'Hosting rights confirmed by the UNFCCC bureau.' },
      { id: 'tl-13', on: '2026-04-02', actor: 'Omar Bin Sulayem', label: 'Advanced to Plan & Build' }
    ],
    gates: {
      assessment: [
        { id: 'g-18', label: 'Eligibility screening completed', kind: 'artefact', status: 'met', owner: 'SDU Assessment' },
        { id: 'g-19', label: 'Director assessment endorsement', kind: 'signoff', status: 'met', owner: 'Hend AlMheiri' }
      ],
      bid: [
        { id: 'g-20', label: 'Host country offer lodged', kind: 'artefact', status: 'met', owner: 'MOCCAE' },
        { id: 'g-21', label: 'Guarantee letters signed', kind: 'signoff', status: 'met', owner: 'Ministry of Finance' }
      ],
      approval: [
        { id: 'g-22', label: 'Funding mandate memorandum issued', kind: 'artefact', status: 'met', owner: 'Ministry of Finance' },
        { id: 'g-23', label: 'All stakeholder sign-offs captured', kind: 'signoff', status: 'met', owner: 'SDU Secretariat' }
      ],
      'plan-build': [
        { id: 'g-24', label: 'Baseline schedule and budget approved', kind: 'artefact', status: 'met', owner: 'SDU Delivery Assurance' },
        { id: 'g-25', label: 'Procurement compliance certificate', kind: 'artefact', status: 'outstanding', owner: 'MOCCAE Procurement', note: 'Certificate rejected 29 Jul; resubmission due.' },
        { id: 'g-26', label: 'No critical risks open', kind: 'artefact', status: 'partial', owner: 'SDU Risk', note: '1 high risk open on cost-to-complete.' },
        { id: 'g-27', label: 'Delivery assurance lead endorsement', kind: 'signoff', status: 'partial', owner: 'Omar Bin Sulayem' }
      ]
    },
    approval: {
      committeeDate: '2026-03-24',
      mandateAed: 1_950_000_000,
      decision: 'approved',
      conditions: ['Quarterly cost-to-complete reporting to the SDU director'],
      stakeholders: [
        { id: 'sh-6', body: 'Ministry of Finance', representative: 'Undersecretary, Budget', status: 'signed', on: '2026-03-11' },
        { id: 'sh-7', body: 'Ministry of Foreign Affairs', representative: 'Director, Multilateral Affairs', status: 'signed', on: '2026-03-06' },
        { id: 'sh-8', body: 'Abu Dhabi Executive Office', representative: 'Director, Major Events', status: 'signed', on: '2026-03-14' },
        { id: 'sh-9', body: 'Ministry of Interior', representative: 'Major Events Security Command', status: 'signed', on: '2026-03-18' }
      ]
    },
    planBuild: {
      baselineApproved: true,
      budgetMandateAed: 1_950_000_000,
      budgetDrawnAed: 742_000_000,
      workstreams: [
        { id: 'ws-1', name: 'Blue zone venue overlay', owner: 'ADNEC Delivery', complete: 41, status: 'on-track' },
        { id: 'ws-2', name: 'Delegate transport & mobility', owner: 'ITC Abu Dhabi', complete: 33, status: 'on-track' },
        { id: 'ws-3', name: 'Accreditation & security platform', owner: 'SDU Technology Cell', complete: 12, status: 'delayed' },
        { id: 'ws-4', name: 'Negotiation programme & protocol', owner: 'MOCCAE', complete: 48, status: 'on-track' },
        { id: 'ws-5', name: 'Sustainability & carbon accounting', owner: 'MOCCAE', complete: 27, status: 'at-risk' },
        { id: 'ws-6', name: 'Broadcast & media operations', owner: 'UAE Media Office', complete: 19, status: 'on-track' }
      ],
      milestones: [
        { id: 'ms-1', label: 'Baseline schedule approved', due: '2026-05-15', status: 'done' },
        { id: 'ms-2', label: 'Overlay design freeze', due: '2026-08-01', status: 'overdue' },
        { id: 'ms-3', label: 'Accreditation platform award', due: '2026-09-12', status: 'due' },
        { id: 'ms-4', label: 'Transport capacity model signed off', due: '2026-10-30', status: 'due' },
        { id: 'ms-5', label: 'Plan & Build gate review', due: '2026-11-20', status: 'due' }
      ],
      risks: [
        { id: 'rk-1', label: 'Cost-to-complete exceeds mandate', severity: 'high', owner: 'SDU Delivery Assurance', mitigation: 'Independent cost review commissioned; value engineering on overlay scope.' },
        { id: 'rk-2', label: 'Accreditation platform integration window compressed', severity: 'high', owner: 'SDU Technology Cell', mitigation: 'Parallel-run legacy platform as contingency through Readiness.' },
        { id: 'rk-3', label: 'Carbon accounting methodology not agreed with UNFCCC', severity: 'medium', owner: 'MOCCAE', mitigation: 'Joint technical working group meeting scheduled September.' }
      ]
    }
  },
  {
    id: 'app-4',
    ref: 'SDU-MEG-2024-008',
    eventName: 'Asian Games 2034 — Abu Dhabi',
    entityId: 'en-8',
    track: 'mega',
    hostBody: 'Olympic Council of Asia',
    emirate: 'Abu Dhabi',
    sector: 'Sport',
    requestedSupportAed: 3_400_000_000,
    editionYear: 2034,
    stage: 'readiness',
    status: 'active',
    stageEnteredOn: '2026-06-15',
    daysInStage: 64,
    slaDueOn: '2026-10-15',
    officer: 'Omar Bin Sulayem',
    aiScore: 87.9,
    summary:
      'Readiness stage for 41 sports across 28 competition venues. Two readiness domains remain below threshold and the integrated assurance sign-off is being held.',
    criteria: [
      { id: 'venue', label: 'Venue & overlay readiness', weight: 20, score: 92, evidence: '26 of 28 venues handed over with overlay complete.' },
      { id: 'transport', label: 'Transport & mobility', weight: 20, score: 88, evidence: 'Games route network tested at 78% peak load.' },
      { id: 'security', label: 'Safety & security', weight: 25, score: 94, evidence: 'Integrated command centre live; accreditation at 91%.' },
      { id: 'workforce', label: 'Workforce & volunteers', weight: 20, score: 74, evidence: '18,400 of 24,000 volunteers trained; retention below plan.' },
      { id: 'technology', label: 'Technology & broadcast', weight: 15, score: 86, evidence: 'Timing and scoring rehearsal passed with two minor issues.' }
    ],
    flags: [
      { id: 'fl-6', severity: 'critical', label: 'Workforce readiness below threshold', detail: 'Volunteer domain at 74 against an 85 threshold; attrition concentrated in venue services.' },
      { id: 'fl-7', severity: 'medium', label: 'Two venues pending handover', detail: 'Aquatics and modern pentathlon venues handover slipped to October.' }
    ],
    recommendations: [
      { id: 're-8', ruleId: 'rr-7', title: 'Hold the Readiness gate and schedule a targeted volunteer rehearsal', detail: 'Workforce domain is 11 points below threshold; a venue-services rehearsal is the highest-yield intervention.', confidence: 95, stage: 'readiness', status: 'accepted' },
      { id: 're-9', ruleId: 'rr-2', title: 'Second an SDU workforce specialist to venue services', detail: 'Attrition is concentrated in one function with no dedicated retention lead.', confidence: 77, stage: 'readiness', status: 'pending' }
    ],
    documents: [
      { id: 'do-14', label: 'Integrated readiness report R-90', status: 'verified', updatedOn: '2026-07-18' },
      { id: 'do-15', label: 'Venue handover certificates (26)', status: 'verified', updatedOn: '2026-08-02' },
      { id: 'do-16', label: 'Volunteer training completion register', status: 'uploaded', updatedOn: '2026-08-14' },
      { id: 'do-17', label: 'Integrated assurance sign-off', status: 'missing', updatedOn: '2026-08-15' }
    ],
    timeline: [
      { id: 'tl-14', on: '2024-03-04', actor: 'Fatima Al Hosani', label: 'Application received' },
      { id: 'tl-15', on: '2024-06-20', actor: 'Hend AlMheiri', label: 'Advanced to Bid' },
      { id: 'tl-16', on: '2024-12-02', actor: 'Hend AlMheiri', label: 'Advanced to Approval', detail: 'Hosting rights awarded by the OCA General Assembly.' },
      { id: 'tl-17', on: '2025-02-10', actor: 'Omar Bin Sulayem', label: 'Advanced to Plan & Build' },
      { id: 'tl-18', on: '2026-06-15', actor: 'Omar Bin Sulayem', label: 'Advanced to Readiness', detail: 'Plan & Build gate cleared with baseline delivered at 97%.' }
    ],
    gates: {
      assessment: [
        { id: 'g-28', label: 'Eligibility screening completed', kind: 'artefact', status: 'met', owner: 'SDU Assessment' },
        { id: 'g-29', label: 'Director assessment endorsement', kind: 'signoff', status: 'met', owner: 'Hend AlMheiri' }
      ],
      bid: [
        { id: 'g-30', label: 'Host-body requirement coverage above 85%', kind: 'artefact', status: 'met', owner: 'Bid Office' },
        { id: 'g-31', label: 'Guarantee letters signed', kind: 'signoff', status: 'met', owner: 'Ministry of Finance' }
      ],
      approval: [
        { id: 'g-32', label: 'Funding mandate memorandum issued', kind: 'artefact', status: 'met', owner: 'Ministry of Finance' },
        { id: 'g-33', label: 'All stakeholder sign-offs captured', kind: 'signoff', status: 'met', owner: 'SDU Secretariat' }
      ],
      'plan-build': [
        { id: 'g-34', label: 'Baseline delivered', kind: 'artefact', status: 'met', owner: 'SDU Delivery Assurance' },
        { id: 'g-35', label: 'No critical risks open', kind: 'artefact', status: 'met', owner: 'SDU Risk' }
      ],
      readiness: [
        { id: 'g-36', label: 'All readiness domains at or above threshold', kind: 'artefact', status: 'outstanding', owner: 'SDU Delivery Assurance', note: 'Workforce domain at 74 against 85 threshold.' },
        { id: 'g-37', label: 'Test event programme completed', kind: 'artefact', status: 'partial', owner: 'Games Operations', note: '9 of 11 test events complete.' },
        { id: 'g-38', label: 'Venue handover certificates issued', kind: 'artefact', status: 'partial', owner: 'Venue Delivery', note: '26 of 28 venues handed over.' },
        { id: 'g-39', label: 'Integrated assurance sign-off', kind: 'signoff', status: 'outstanding', owner: 'Independent Assurance Panel' }
      ]
    },
    approval: {
      committeeDate: '2025-01-20',
      mandateAed: 3_400_000_000,
      decision: 'approved',
      conditions: [],
      stakeholders: [
        { id: 'sh-10', body: 'Ministry of Finance', representative: 'Undersecretary, Budget', status: 'signed', on: '2025-01-08' },
        { id: 'sh-11', body: 'Abu Dhabi Executive Office', representative: 'Director, Major Events', status: 'signed', on: '2025-01-12' },
        { id: 'sh-12', body: 'Ministry of Interior', representative: 'Major Events Security Command', status: 'signed', on: '2025-01-15' },
        { id: 'sh-13', body: 'GCAA — Aviation', representative: 'Director, Airspace', status: 'signed', on: '2025-01-16' }
      ]
    },
    planBuild: {
      baselineApproved: true,
      budgetMandateAed: 3_400_000_000,
      budgetDrawnAed: 2_986_000_000,
      workstreams: [
        { id: 'ws-7', name: 'Competition venues', owner: 'Venue Delivery', complete: 94, status: 'on-track' },
        { id: 'ws-8', name: 'Games route network', owner: 'ITC Abu Dhabi', complete: 91, status: 'on-track' },
        { id: 'ws-9', name: 'Athlete village', owner: 'Modon', complete: 97, status: 'on-track' },
        { id: 'ws-10', name: 'Workforce & volunteers', owner: 'ADSC People', complete: 77, status: 'at-risk' }
      ],
      milestones: [
        { id: 'ms-6', label: 'Athlete village handover', due: '2026-05-30', status: 'done' },
        { id: 'ms-7', label: 'Final two venue handovers', due: '2026-10-10', status: 'due' },
        { id: 'ms-8', label: 'Readiness gate review', due: '2026-10-15', status: 'due' }
      ],
      risks: [
        { id: 'rk-4', label: 'Volunteer attrition in venue services', severity: 'high', owner: 'ADSC People', mitigation: 'Retention incentive and rehearsal programme approved.' }
      ]
    },
    readiness: {
      assuranceSignedOff: false,
      domains: [
        { id: 'rd-1', label: 'Venue & overlay', score: 92, threshold: 85, owner: 'Venue Delivery' },
        { id: 'rd-2', label: 'Transport & mobility', score: 88, threshold: 85, owner: 'ITC Abu Dhabi' },
        { id: 'rd-3', label: 'Safety & security', score: 94, threshold: 90, owner: 'Security Command' },
        { id: 'rd-4', label: 'Workforce & volunteers', score: 74, threshold: 85, owner: 'ADSC People' },
        { id: 'rd-5', label: 'Technology & broadcast', score: 86, threshold: 85, owner: 'Games Technology' },
        { id: 'rd-6', label: 'Guest experience & services', score: 83, threshold: 85, owner: 'Games Services' }
      ],
      tests: [
        { id: 'te-1', label: 'Aquatics test event', on: '2026-04-18', result: 'passed' },
        { id: 'te-2', label: 'Games route network peak-load test', on: '2026-05-22', result: 'issues' },
        { id: 'te-3', label: 'Opening ceremony technical rehearsal', on: '2026-07-09', result: 'passed' },
        { id: 'te-4', label: 'Venue services surge rehearsal', on: '2026-09-05', result: 'scheduled' },
        { id: 'te-5', label: 'Integrated command exercise', on: '2026-09-26', result: 'scheduled' }
      ]
    }
  },
  {
    id: 'app-5',
    ref: 'SDU-MAJ-2023-019',
    eventName: 'World Energy Congress 2026',
    entityId: 'en-1',
    track: 'major',
    hostBody: 'World Energy Council',
    emirate: 'Abu Dhabi',
    sector: 'Energy',
    requestedSupportAed: 320_000_000,
    editionYear: 2026,
    stage: 'deliver-operate',
    status: 'active',
    stageEnteredOn: '2026-08-10',
    daysInStage: 8,
    slaDueOn: '2026-09-05',
    officer: 'Omar Bin Sulayem',
    aiScore: 90.1,
    summary:
      'Live delivery. Day 4 of 6 operating days with 41,200 accredited attendees on site. Closure reporting and legacy benefit capture begin at the end of the operating window.',
    criteria: [
      { id: 'strategic-fit', label: 'Sector strategy alignment', weight: 25, score: 92, evidence: 'Anchored to the UAE Energy Strategy 2050.' },
      { id: 'economic-impact', label: 'Economic & reputational impact', weight: 20, score: 89, evidence: '96,000 international visitor nights delivered to date.' },
      { id: 'entity-capability', label: 'Entity delivery capability', weight: 20, score: 94, evidence: 'DCT Abu Dhabi operating with ADNEC delivery partner.' },
      { id: 'infrastructure', label: 'Venue & logistics fit', weight: 15, score: 88, evidence: 'ADNEC halls 4–11 with no overlay defects reported.' },
      { id: 'financial', label: 'Financial viability', weight: 20, score: 87, evidence: 'Commercial revenue 12% ahead of plan.' }
    ],
    flags: [
      { id: 'fl-8', severity: 'medium', label: 'Shuttle service level dipped on day 3', detail: 'Service level 88% on the corniche route; additional fleet deployed for day 4.' }
    ],
    recommendations: [
      { id: 're-10', ruleId: 'rr-8', title: 'Monitor shuttle service level before deploying the surge team', detail: 'Single-day dip; threshold requires two consecutive days below 90% before surge deployment.', confidence: 68, stage: 'deliver-operate', status: 'pending' }
    ],
    documents: [
      { id: 'do-18', label: 'Daily operations report — Day 3', status: 'verified', updatedOn: '2026-08-17' },
      { id: 'do-19', label: 'Legacy benefits baseline', status: 'uploaded', updatedOn: '2026-08-01' },
      { id: 'do-20', label: 'Financial close-out pack', status: 'missing', updatedOn: '2026-08-10' }
    ],
    timeline: [
      { id: 'tl-19', on: '2023-11-02', actor: 'Fatima Al Hosani', label: 'Application received' },
      { id: 'tl-20', on: '2024-02-14', actor: 'Hend AlMheiri', label: 'Advanced to Bid' },
      { id: 'tl-21', on: '2024-07-30', actor: 'Hend AlMheiri', label: 'Advanced to Approval' },
      { id: 'tl-22', on: '2024-10-05', actor: 'Omar Bin Sulayem', label: 'Advanced to Plan & Build' },
      { id: 'tl-23', on: '2026-03-11', actor: 'Omar Bin Sulayem', label: 'Advanced to Readiness' },
      { id: 'tl-24', on: '2026-08-10', actor: 'Omar Bin Sulayem', label: 'Advanced to Deliver & Operate' }
    ],
    gates: {
      assessment: [
        { id: 'g-40', label: 'Eligibility screening completed', kind: 'artefact', status: 'met', owner: 'SDU Assessment' },
        { id: 'g-41', label: 'Director assessment endorsement', kind: 'signoff', status: 'met', owner: 'Hend AlMheiri' }
      ],
      bid: [
        { id: 'g-42', label: 'Host-body requirement coverage above 85%', kind: 'artefact', status: 'met', owner: 'Bid Office' },
        { id: 'g-43', label: 'Guarantee letters signed', kind: 'signoff', status: 'met', owner: 'Ministry of Finance' }
      ],
      approval: [
        { id: 'g-44', label: 'Funding mandate memorandum issued', kind: 'artefact', status: 'met', owner: 'Ministry of Finance' },
        { id: 'g-45', label: 'All stakeholder sign-offs captured', kind: 'signoff', status: 'met', owner: 'SDU Secretariat' }
      ],
      'plan-build': [
        { id: 'g-46', label: 'Baseline delivered', kind: 'artefact', status: 'met', owner: 'SDU Delivery Assurance' },
        { id: 'g-47', label: 'No critical risks open', kind: 'artefact', status: 'met', owner: 'SDU Risk' }
      ],
      readiness: [
        { id: 'g-48', label: 'All readiness domains at or above threshold', kind: 'artefact', status: 'met', owner: 'SDU Delivery Assurance' },
        { id: 'g-49', label: 'Integrated assurance sign-off', kind: 'signoff', status: 'met', owner: 'Independent Assurance Panel' }
      ],
      'deliver-operate': [
        { id: 'g-50', label: 'Daily operations reporting maintained', kind: 'artefact', status: 'met', owner: 'Event Operations' },
        { id: 'g-51', label: 'Legacy benefits register completed', kind: 'artefact', status: 'partial', owner: 'DCT Abu Dhabi', note: 'Baseline captured; post-event actuals pending.' },
        { id: 'g-52', label: 'Financial close-out pack issued', kind: 'artefact', status: 'outstanding', owner: 'DCT Finance' },
        { id: 'g-53', label: 'Closure report issued to the SDU director', kind: 'signoff', status: 'outstanding', owner: 'Omar Bin Sulayem' }
      ]
    },
    readiness: {
      assuranceSignedOff: true,
      domains: [
        { id: 'rd-7', label: 'Venue & overlay', score: 95, threshold: 85, owner: 'ADNEC' },
        { id: 'rd-8', label: 'Transport & mobility', score: 89, threshold: 85, owner: 'ITC Abu Dhabi' },
        { id: 'rd-9', label: 'Safety & security', score: 96, threshold: 90, owner: 'Security Command' },
        { id: 'rd-10', label: 'Workforce & volunteers', score: 91, threshold: 85, owner: 'DCT People' },
        { id: 'rd-11', label: 'Technology & broadcast', score: 93, threshold: 85, owner: 'Event Technology' },
        { id: 'rd-12', label: 'Guest experience & services', score: 90, threshold: 85, owner: 'Event Services' }
      ],
      tests: [
        { id: 'te-6', label: 'Registration and accreditation dry run', on: '2026-06-20', result: 'passed' },
        { id: 'te-7', label: 'Plenary technical rehearsal', on: '2026-07-25', result: 'passed' },
        { id: 'te-8', label: 'Command centre exercise', on: '2026-08-02', result: 'passed' }
      ]
    },
    delivery: {
      operatingDay: 4,
      operatingDays: 6,
      attendanceToday: 41_200,
      attendanceTarget: 38_000,
      serviceLevel: 93,
      incidents: [
        { id: 'in-1', label: 'Corniche shuttle route delay — day 3 peak', severity: 'medium', status: 'resolved' },
        { id: 'in-2', label: 'Hall 7 catering queue exceeded 20 minutes', severity: 'medium', status: 'open' },
        { id: 'in-3', label: 'Delegate wifi saturation in plenary', severity: 'high', status: 'resolved' }
      ],
      legacyBenefits: [
        { id: 'lb-1', label: 'Signed energy sector MoUs', value: '31 agreements' },
        { id: 'lb-2', label: 'International visitor nights delivered', value: '96,000' },
        { id: 'lb-3', label: 'Permanent ADNEC overlay assets retained', value: 'AED 42M value' },
        { id: 'lb-4', label: 'Trained event workforce retained in sector', value: '1,240 people' }
      ],
      closureReportIssued: false
    }
  },
  {
    id: 'app-6',
    ref: 'SDU-MEG-2026-021',
    eventName: 'Youth Olympic Games 2034 — UAE Bid',
    entityId: 'en-4',
    track: 'mega',
    hostBody: 'International Olympic Committee',
    emirate: 'Federal',
    sector: 'Sport',
    requestedSupportAed: 1_100_000_000,
    editionYear: 2034,
    stage: 'assessment',
    status: 'in-review',
    stageEnteredOn: '2026-07-29',
    daysInStage: 20,
    slaDueOn: '2026-08-24',
    officer: 'Fatima Al Hosani',
    aiScore: 68.3,
    summary:
      'Federal expression of interest for a multi-emirate Youth Olympic Games. Co-funding structure is thin and the entity is under compliance review following the 2030 bid.',
    criteria: [
      { id: 'strategic-fit', label: 'Strategic fit with national agenda', weight: 25, score: 78, evidence: 'Supports youth sport participation targets; not named in current sector strategy.' },
      { id: 'economic-impact', label: 'Economic impact & GDP contribution', weight: 20, score: 61, evidence: 'Modelled 0.7x return on requested support over 5 years.' },
      { id: 'entity-capability', label: 'Entity delivery capability', weight: 15, score: 58, evidence: 'Entity under compliance review; no delivered event above AED 50M in 5 years.' },
      { id: 'infrastructure', label: 'Infrastructure & venue readiness', weight: 15, score: 84, evidence: 'Existing Asian Games 2034 venue programme largely reusable.' },
      { id: 'financial', label: 'Financial viability & co-funding', weight: 15, score: 54, evidence: 'Co-funding at 11% with no signed sponsorship commitments.' },
      { id: 'legacy', label: 'Legacy & sustainability', weight: 10, score: 79, evidence: 'Youth participation legacy programme drafted with Ministry of Sport.' }
    ],
    flags: [
      { id: 'fl-9', severity: 'critical', label: 'Co-funding below Mega threshold', detail: 'Co-funding at 11% against a 30% minimum for Mega events.' },
      { id: 'fl-10', severity: 'high', label: 'Entity compliance under review', detail: 'Outstanding audit findings from the 2030 bid remain unresolved.' },
      { id: 'fl-11', severity: 'medium', label: 'Venue programme dependency', detail: 'Reuse assumption depends on Asian Games 2034 delivery completing to plan.' }
    ],
    recommendations: [
      { id: 're-11', ruleId: 'rr-1', title: 'Request a revised funding structure before advancing to Bid', detail: 'Score sits in the manual-review band with co-funding at 11%. Historically 88% of similar requests are resolved within one cycle.', confidence: 89, stage: 'assessment', status: 'pending' },
      { id: 're-12', ruleId: 'rr-2', title: 'Attach an SDU delivery partner to the entity for the lifecycle', detail: 'No delivered event above AED 50M in the last five years.', confidence: 81, stage: 'assessment', status: 'pending' }
    ],
    documents: [
      { id: 'do-21', label: 'Expression of interest', status: 'verified', updatedOn: '2026-07-29' },
      { id: 'do-22', label: 'Venue reuse assessment', status: 'uploaded', updatedOn: '2026-08-05' },
      { id: 'do-23', label: 'Co-funding commitment letters', status: 'missing', updatedOn: '2026-08-05' },
      { id: 'do-24', label: 'Entity governance audit response', status: 'missing', updatedOn: '2026-08-05' }
    ],
    timeline: [
      { id: 'tl-25', on: '2026-07-29', actor: 'Fatima Al Hosani', label: 'Application received' },
      { id: 'tl-26', on: '2026-08-03', actor: 'AI Assessment Engine', label: 'Assessment score generated', detail: 'Weighted score 68.3 — manual review band.' }
    ],
    gates: {
      assessment: [
        { id: 'g-54', label: 'Eligibility screening completed', kind: 'artefact', status: 'met', owner: 'SDU Assessment' },
        { id: 'g-55', label: 'Economic impact model reviewed', kind: 'artefact', status: 'partial', owner: 'Ministry of Economy', note: 'Return on support below 1.0x — review requested.' },
        { id: 'g-56', label: 'Co-funding commitment letters received', kind: 'artefact', status: 'outstanding', owner: 'UAE NOC' },
        { id: 'g-57', label: 'Director assessment endorsement', kind: 'signoff', status: 'outstanding', owner: 'Hend AlMheiri' }
      ]
    }
  },
  {
    id: 'app-7',
    ref: 'SDU-MAJ-2026-033',
    eventName: 'World Ophthalmology Congress 2029',
    entityId: 'en-5',
    track: 'major',
    hostBody: 'International Council of Ophthalmology',
    emirate: 'Sharjah',
    sector: 'Healthcare',
    requestedSupportAed: 96_000_000,
    editionYear: 2029,
    stage: 'assessment',
    status: 'in-review',
    stageEnteredOn: '2026-08-06',
    daysInStage: 12,
    slaDueOn: '2026-09-01',
    officer: 'Fatima Al Hosani',
    aiScore: 79.6,
    summary:
      'Sharjah bid for an 18,000-delegate medical congress with Expo Centre Sharjah as host venue and a regional training legacy programme.',
    criteria: [
      { id: 'strategic-fit', label: 'Sector strategy alignment', weight: 25, score: 84, evidence: 'Aligned to the national health sector knowledge economy pillar.' },
      { id: 'economic-impact', label: 'Economic & reputational impact', weight: 20, score: 77, evidence: '54,000 international visitor nights modelled.' },
      { id: 'entity-capability', label: 'Entity delivery capability', weight: 20, score: 81, evidence: 'Shurooq has delivered three international congresses since 2022.' },
      { id: 'infrastructure', label: 'Venue & logistics fit', weight: 15, score: 74, evidence: 'Venue capacity adequate; hotel inventory within 20 minutes is tight.' },
      { id: 'financial', label: 'Financial viability', weight: 20, score: 80, evidence: 'Co-funding at 42% with two signed sponsors.' }
    ],
    flags: [
      { id: 'fl-12', severity: 'medium', label: 'Accommodation proximity', detail: '4-star and above inventory within 20 minutes covers 61% of delegate demand.' }
    ],
    recommendations: [
      { id: 're-13', ruleId: 'rr-1', title: 'Advance to Bid with an accommodation mitigation plan attached', detail: 'Score is in the auto-advance band; the only material gap is delegate accommodation proximity.', confidence: 84, stage: 'assessment', status: 'pending' }
    ],
    documents: [
      { id: 'do-25', label: 'Application form and event concept', status: 'verified', updatedOn: '2026-08-06' },
      { id: 'do-26', label: 'Venue capacity letter', status: 'verified', updatedOn: '2026-08-08' },
      { id: 'do-27', label: 'Co-funding commitment letters', status: 'uploaded', updatedOn: '2026-08-12' },
      { id: 'do-28', label: 'Accommodation inventory study', status: 'missing', updatedOn: '2026-08-12' }
    ],
    timeline: [
      { id: 'tl-27', on: '2026-08-06', actor: 'Fatima Al Hosani', label: 'Application received' },
      { id: 'tl-28', on: '2026-08-09', actor: 'AI Assessment Engine', label: 'Assessment score generated', detail: 'Weighted score 79.6 — auto-advance band.' }
    ],
    gates: {
      assessment: [
        { id: 'g-58', label: 'Eligibility screening completed', kind: 'artefact', status: 'met', owner: 'SDU Assessment' },
        { id: 'g-59', label: 'Venue capacity confirmation', kind: 'artefact', status: 'met', owner: 'Expo Centre Sharjah' },
        { id: 'g-60', label: 'Accommodation inventory study', kind: 'artefact', status: 'outstanding', owner: 'Shurooq' },
        { id: 'g-61', label: 'Director assessment endorsement', kind: 'signoff', status: 'partial', owner: 'Hend AlMheiri' }
      ]
    }
  },
  {
    id: 'app-8',
    ref: 'SDU-MAJ-2026-029',
    eventName: 'Global Space Congress 2028',
    entityId: 'en-1',
    track: 'major',
    hostBody: 'International Astronautical Federation',
    emirate: 'Abu Dhabi',
    sector: 'Space & Advanced Technology',
    requestedSupportAed: 148_000_000,
    editionYear: 2028,
    stage: 'assessment',
    status: 'info-requested',
    stageEnteredOn: '2026-07-14',
    daysInStage: 35,
    slaDueOn: '2026-08-16',
    officer: 'Khalid Al Marzouqi',
    aiScore: 72.1,
    summary:
      'Bid to host the IAF congress in Abu Dhabi. Information request issued on the security clearance framework for delegations under export-control restrictions.',
    criteria: [
      { id: 'strategic-fit', label: 'Sector strategy alignment', weight: 25, score: 88, evidence: 'Core to the National Space Strategy 2030.' },
      { id: 'economic-impact', label: 'Economic & reputational impact', weight: 20, score: 71, evidence: '28,000 international visitor nights; high media value.' },
      { id: 'entity-capability', label: 'Entity delivery capability', weight: 20, score: 86, evidence: 'DCT Abu Dhabi with UAE Space Agency as technical partner.' },
      { id: 'infrastructure', label: 'Venue & logistics fit', weight: 15, score: 79, evidence: 'ADNEC halls confirmed for the proposed dates.' },
      { id: 'financial', label: 'Financial viability', weight: 20, score: 48, evidence: 'Co-funding at 14%; sponsorship pipeline not evidenced.' }
    ],
    flags: [
      { id: 'fl-13', severity: 'high', label: 'Co-funding not evidenced', detail: 'No signed sponsorship or partner contributions submitted with the application.' },
      { id: 'fl-14', severity: 'high', label: 'Delegation clearance framework absent', detail: 'Export-control sensitive delegations require an agreed clearance framework with MoFA.' }
    ],
    recommendations: [
      { id: 're-14', ruleId: 'rr-1', title: 'Hold at Assessment until the funding structure is revised', detail: 'Financial criterion at 48 drags the weighted score into the manual-review band.', confidence: 87, stage: 'assessment', status: 'accepted' }
    ],
    documents: [
      { id: 'do-29', label: 'Application form and event concept', status: 'verified', updatedOn: '2026-07-14' },
      { id: 'do-30', label: 'Venue availability letter', status: 'verified', updatedOn: '2026-07-17' },
      { id: 'do-31', label: 'Delegation clearance framework', status: 'missing', updatedOn: '2026-07-30' },
      { id: 'do-32', label: 'Co-funding commitment letters', status: 'missing', updatedOn: '2026-07-30' }
    ],
    timeline: [
      { id: 'tl-29', on: '2026-07-14', actor: 'Khalid Al Marzouqi', label: 'Application received' },
      { id: 'tl-30', on: '2026-07-19', actor: 'AI Assessment Engine', label: 'Assessment score generated', detail: 'Weighted score 72.1 — manual review band.' },
      { id: 'tl-31', on: '2026-07-30', actor: 'Khalid Al Marzouqi', label: 'Information requested', detail: 'Co-funding evidence and delegation clearance framework requested from the entity.' }
    ],
    gates: {
      assessment: [
        { id: 'g-62', label: 'Eligibility screening completed', kind: 'artefact', status: 'met', owner: 'SDU Assessment' },
        { id: 'g-63', label: 'Co-funding commitment letters received', kind: 'artefact', status: 'outstanding', owner: 'DCT Abu Dhabi' },
        { id: 'g-64', label: 'Delegation clearance framework agreed', kind: 'artefact', status: 'outstanding', owner: 'Ministry of Foreign Affairs' },
        { id: 'g-65', label: 'Director assessment endorsement', kind: 'signoff', status: 'outstanding', owner: 'Hend AlMheiri' }
      ]
    }
  }
]
